/**
 * @file rx-v1.schema.js
 * @description Schéma Zod du wrapper commun `rx.v1` — contrat de réponse déclaratif
 *              partagé entre backend (Node.js/Express) et frontend (React/CRACO).
 *              Monosource unique pour la validation runtime front + back.
 *
 * @philosophy
 *  - Ce fichier N'EMET PAS de HTML/Markdown : il décrit la STRUCTURE d'une réponse.
 *  - Le frontend décide du rendu (composant colocated) en lisant `template`.
 *  - Le LLM ne produit JAMAIS ce payload directement — l'orchestrator le construit
 *    de manière déterministe à partir des tool_calls (§20 Design System v1.1 FINAL).
 *
 * @spec RX_v1.1-FINAL — §3.3 (DSL rx.v1), §16.0 (taxonomie), §21 (AYA Native Exp.)
 *
 * @noTypeScript Fichier JS pur avec JSDoc types (choix Q4=B utilisateur 2026-07-01).
 */

const { z } = require('zod');

// ─────────────────────────────────────────────────────────────────────────────
// Sous-schémas communs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Priority — classement d'urgence visuelle de la réponse.
 * Impacte le style du template (bordures, banner, motion).
 */
const PrioritySchema = z.enum(['low', 'normal', 'high', 'critical']);

/**
 * Layout — hint de mise en page pour le TemplateRouter.
 * Le composant colocated garde la main sur le CSS final, mais peut lire ce hint.
 */
const LayoutSchema = z.enum(['single_card', 'card_grid', 'list', 'banner', 'modal', 'form']);

/**
 * Action déclarative — l'utilisateur peut cliquer sur un CTA affiché par le template.
 * L'orchestrator ne connaît pas l'implémentation frontend : il fournit un `action_id`
 * que le frontend mappe à un handler React (via `onAction(action_id, params)`).
 */
const ActionSchema = z.object({
  action_id: z.string().min(1),
  label: z.string().min(1),
  params: z.record(z.any()).default({}),
  style: z.enum(['primary', 'secondary', 'ghost', 'danger']).default('primary'),
});

/**
 * Meta — informations de traçabilité (audit, latence, source).
 * `hmac` est optionnel et sera ajouté en post-P0 (chaîne HMAC de §7 Design System).
 */
const MetaSchema = z.object({
  generated_at: z.string().datetime({ offset: true }),
  source: z.enum(['orchestrator', 'llm', 'mock', 'preview_fixture']),
  latency_ms: z.number().int().nonnegative().optional(),
  entry_id: z.string().optional(),
  hmac: z.string().length(64).optional(), // sha256 hex, ajouté en post-P0
  fallback_text: z.string().optional(),   // Markdown legacy pour clients non-rich
  loading: z.boolean().optional(),        // UX-only : skeleton côté frontend
  preview_state: z.enum(['initial', 'validating', 'success', 'error', 'loading', 'empty']).optional(), // UX-only : forcer un état visuel en preview (/rx/preview)
});

// ─────────────────────────────────────────────────────────────────────────────
// Wrapper rx.v1 — enveloppe universelle de toute réponse déclarative
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Payload rx.v1 générique. Chaque template étend `sections` avec un schéma spécifique.
 *
 * @typedef {object} RxV1Payload
 * @property {"rx.v1"} schema_version
 * @property {string} template - Nom du template (ex: "merchant_preview")
 * @property {"single_card"|"card_grid"|"list"|"banner"|"modal"} layout
 * @property {"low"|"normal"|"high"|"critical"} priority
 * @property {Array} sections - Contenu template-specific (validé par le schéma du template)
 * @property {Array<{action_id: string, label: string, params: object, style: string}>} actions
 * @property {string} [footer] - Texte optionnel en pied de payload
 * @property {object} meta - Traçabilité (generated_at, source, latency_ms, ...)
 */
const RxV1WrapperSchema = z.object({
  schema_version: z.literal('rx.v1'),
  template: z.string().min(1).regex(/^[a-z][a-z0-9_]*$/, 'template must be snake_case'),
  layout: LayoutSchema,
  priority: PrioritySchema.default('normal'),
  sections: z.array(z.unknown()).min(0), // validé par schéma template en aval
  actions: z.array(ActionSchema).default([]),
  footer: z.string().optional(),
  meta: MetaSchema,
});

// ─────────────────────────────────────────────────────────────────────────────
// Fonctions utilitaires
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Valide un payload rx.v1 générique (sans validation template-specific).
 * Utilisé côté backend orchestrator avant émission, côté frontend avant rendu.
 *
 * @param {unknown} payload
 * @returns {{ success: true, data: RxV1Payload } | { success: false, error: import('zod').ZodError }}
 */
function safeParseRxV1(payload) {
  return RxV1WrapperSchema.safeParse(payload);
}

/**
 * Version stricte qui throw en cas d'erreur.
 *
 * @param {unknown} payload
 * @returns {RxV1Payload}
 * @throws {import('zod').ZodError}
 */
function parseRxV1(payload) {
  return RxV1WrapperSchema.parse(payload);
}

// ─────────────────────────────────────────────────────────────────────────────
// Exports (CommonJS — compatible backend Node.js + frontend via CRACO alias)
// ─────────────────────────────────────────────────────────────────────────────

module.exports = {
  RxV1WrapperSchema,
  PrioritySchema,
  LayoutSchema,
  ActionSchema,
  MetaSchema,
  safeParseRxV1,
  parseRxV1,
  RX_SCHEMA_VERSION: 'rx.v1',
};
